/**
 * Organization hierarchy helper utilities
 * Constants, limits, and role definitions for Organizations, Workspaces, and Groups
 */

// ==================== LIMITS ====================

export const ORGANIZATION_LIMITS = {
    workspaces: {
        rp_pro: 5,
        ultra: 20,
        enterprise: 100
    },
    groups_per_workspace: {
        rp_pro: 10,
        ultra: 10,
        enterprise: 50
    },
    members: {
        rp_pro: 10,
        ultra: 100,
        enterprise: 1000
    },
    api_keys: {
        rp_pro: 0,
        ultra: 10,
        enterprise: 50
    },
    webhooks: {
        rp_pro: 5,
        ultra: 5,
        enterprise: 25
    },
    max_organizations: {
        rp_pro: 3,
        ultra: 10,
        enterprise: 50
    }
};

// ==================== ROLES ====================

export const ORG_ROLES = {
    OWNER: 'owner',
    ADMIN: 'admin',
    MANAGER: 'manager',
    MEMBER: 'member',
    VIEWER: 'viewer'
};

export const ORG_ROLE_LABELS = {
    [ORG_ROLES.OWNER]: 'Owner',
    [ORG_ROLES.ADMIN]: 'Admin',
    [ORG_ROLES.MANAGER]: 'Manager',
    [ORG_ROLES.MEMBER]: 'Member',
    [ORG_ROLES.VIEWER]: 'Viewer'
};

export const ORG_ROLE_DESCRIPTIONS = {
    [ORG_ROLES.OWNER]: 'Full control over the organization',
    [ORG_ROLES.ADMIN]: 'Manage workspaces, groups, and members',
    [ORG_ROLES.MANAGER]: 'Manage groups and members within assigned workspaces',
    [ORG_ROLES.MEMBER]: 'Access to assigned workspaces and groups',
    [ORG_ROLES.VIEWER]: 'Read-only access to assigned resources'
};

// Roles available for selection (excluding owner which is auto-assigned)
export const ASSIGNABLE_ROLES = [
    ORG_ROLES.ADMIN,
    ORG_ROLES.MANAGER,
    ORG_ROLES.MEMBER,
    ORG_ROLES.VIEWER
];

// ==================== MEMBER STATUS ====================

export const MEMBER_STATUS = {
    ACTIVE: 'Active',
    PENDING: 'Pending',
    SUSPENDED: 'Suspended'
};

export const MEMBER_STATUS_COLORS = {
    [MEMBER_STATUS.ACTIVE]: {
        bg: 'dark:bg-emerald-500/10 bg-emerald-50',
        text: 'dark:text-emerald-400 text-emerald-700',
        border: 'dark:border-transparent border-emerald-200'
    },
    [MEMBER_STATUS.PENDING]: {
        bg: 'dark:bg-amber-500/10 bg-amber-50',
        text: 'dark:text-amber-400 text-amber-700',
        border: 'dark:border-transparent border-amber-200'
    },
    [MEMBER_STATUS.SUSPENDED]: {
        bg: 'dark:bg-red-500/10 bg-red-50',
        text: 'dark:text-red-400 text-red-700',
        border: 'dark:border-transparent border-red-200'
    }
};

// ==================== WEBHOOK EVENTS ====================

export const WEBHOOK_EVENTS = [
    { id: 'case.created', label: 'Case Created', description: 'Triggered when a new case is created' },
    { id: 'case.updated', label: 'Case Updated', description: 'Triggered when a case is modified' },
    { id: 'case.status_changed', label: 'Case Status Changed', description: 'Triggered when case status changes' },
    { id: 'document.uploaded', label: 'Document Uploaded', description: 'Triggered when a document is uploaded' },
    { id: 'report.completed', label: 'Report Completed', description: 'Triggered when a report finishes processing' },
    { id: 'member.invited', label: 'Member Invited', description: 'Triggered when a member is invited' },
    { id: 'member.joined', label: 'Member Joined', description: 'Triggered when a member accepts invitation' },
    { id: 'member.removed', label: 'Member Removed', description: 'Triggered when a member is removed' }
];

// ==================== HELPER FUNCTIONS ====================

/**
 * Get organization limits based on tier
 * @param {string} tier - Subscription tier (ultra or enterprise)
 * @returns {object} Limits object
 */
export const getOrganizationLimits = (tier) => {
    const normalizedTier = (tier || 'ultra').toLowerCase();
    const tierKey = normalizedTier === 'enterprise' ? 'enterprise' : 'ultra';

    return {
        workspaces: ORGANIZATION_LIMITS.workspaces[tierKey],
        groupsPerWorkspace: ORGANIZATION_LIMITS.groups_per_workspace[tierKey],
        members: ORGANIZATION_LIMITS.members[tierKey],
        apiKeys: ORGANIZATION_LIMITS.api_keys[tierKey],
        webhooks: ORGANIZATION_LIMITS.webhooks[tierKey]
    };
};

/**
 * Check if user can perform action based on role
 * @param {string} userRole - User's role in the organization
 * @param {string} requiredRole - Minimum role required for action
 * @returns {boolean}
 */
export const hasRolePermission = (userRole, requiredRole) => {
    const roleHierarchy = {
        [ORG_ROLES.OWNER]: 4,
        [ORG_ROLES.ADMIN]: 3,
        [ORG_ROLES.MANAGER]: 2,
        [ORG_ROLES.MEMBER]: 1,
        [ORG_ROLES.VIEWER]: 0
    };

    const userLevel = roleHierarchy[userRole] ?? 0;
    const requiredLevel = roleHierarchy[requiredRole] ?? 0;

    return userLevel >= requiredLevel;
};

/**
 * Check if user can manage another user (invite, remove, change role)
 * @param {string} actorRole - Role of the user performing the action
 * @param {string} targetRole - Role of the user being acted upon
 * @returns {boolean}
 */
export const canManageUser = (actorRole, targetRole) => {
    // Can't manage yourself or users at same/higher level
    // Owner can manage everyone except other owners
    // Admins can manage managers, members, viewers
    // Managers can manage members, viewers
    const roleHierarchy = {
        [ORG_ROLES.OWNER]: 4,
        [ORG_ROLES.ADMIN]: 3,
        [ORG_ROLES.MANAGER]: 2,
        [ORG_ROLES.MEMBER]: 1,
        [ORG_ROLES.VIEWER]: 0
    };

    const actorLevel = roleHierarchy[actorRole] ?? 0;
    const targetLevel = roleHierarchy[targetRole] ?? 0;

    return actorLevel > targetLevel;
};

/**
 * Get available roles that a user can assign to others
 * @param {string} userRole - Role of the user assigning roles
 * @returns {string[]} Array of assignable roles
 */
export const getAssignableRoles = (userRole) => {
    const roleHierarchy = {
        [ORG_ROLES.OWNER]: 4,
        [ORG_ROLES.ADMIN]: 3,
        [ORG_ROLES.MANAGER]: 2,
        [ORG_ROLES.MEMBER]: 1,
        [ORG_ROLES.VIEWER]: 0
    };

    const userLevel = roleHierarchy[userRole] ?? 0;

    return ASSIGNABLE_ROLES.filter(role => {
        const roleLevel = roleHierarchy[role] ?? 0;
        return roleLevel < userLevel;
    });
};

/**
 * Format member count with limit
 * @param {number} count - Current count
 * @param {number} limit - Maximum limit
 * @returns {string} Formatted string like "5 / 100"
 */
export const formatMemberCount = (count, limit) => {
    return `${count} / ${limit}`;
};

/**
 * Check if limit is reached
 * @param {number} count - Current count
 * @param {number} limit - Maximum limit
 * @returns {boolean}
 */
export const isLimitReached = (count, limit) => {
    return count >= limit;
};

/**
 * Get workspace icon color based on index
 * @param {number} index - Workspace index
 * @returns {string} Tailwind color classes
 */
export const getWorkspaceColor = (index) => {
    const colors = [
        'from-teal-400 to-teal-600',
        'from-blue-400 to-blue-600',
        'from-purple-400 to-purple-600',
        'from-pink-400 to-pink-600',
        'from-orange-400 to-orange-600',
        'from-green-400 to-green-600',
        'from-indigo-400 to-indigo-600',
        'from-rose-400 to-rose-600'
    ];
    return colors[index % colors.length];
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials (up to 2 characters)
 */
export const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Format webhook event for display
 * @param {string} eventId - Event ID like 'case.created'
 * @returns {string} Formatted label
 */
export const formatWebhookEvent = (eventId) => {
    const event = WEBHOOK_EVENTS.find(e => e.id === eventId);
    return event?.label || eventId;
};
